import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DateTimePickerProps {
  value: string; // ISO datetime-local format: YYYY-MM-DDTHH:mm
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function pad(n: number) {
  return n.toString().padStart(2, '0');
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

export function DateTimePicker({ value, onChange, placeholder = 'Select date & time', className }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Parse value or use current date as default view
  const parsed = value ? new Date(value) : null;
  const now = new Date();

  const [viewYear, setViewYear] = useState(parsed?.getFullYear() ?? now.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.getMonth() ?? now.getMonth());
  const [selectedDay, setSelectedDay] = useState(parsed?.getDate() ?? 0);
  const [hour, setHour] = useState(parsed ? parsed.getHours() : 12);
  const [minute, setMinute] = useState(parsed ? parsed.getMinutes() : 0);

  // Sync state when value changes externally
  useEffect(() => {
    if (value) {
      const d = new Date(value);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
      setSelectedDay(d.getDate());
      setHour(d.getHours());
      setMinute(d.getMinutes());
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function emitChange(day: number, h: number, m: number) {
    const iso = `${viewYear}-${pad(viewMonth + 1)}-${pad(day)}T${pad(h)}:${pad(m)}`;
    onChange(iso);
  }

  function handleDayClick(day: number) {
    setSelectedDay(day);
    emitChange(day, hour, minute);
  }

  function handleTimeChange(h: number, m: number) {
    setHour(h);
    setMinute(m);
    if (selectedDay > 0) {
      emitChange(selectedDay, h, m);
    }
  }

  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  }

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const today = new Date();
  const isToday = (day: number) =>
    viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate();
  const isSelected = (day: number) =>
    parsed && viewYear === parsed.getFullYear() && viewMonth === parsed.getMonth() && day === parsed.getDate();

  const displayValue = parsed
    ? `${MONTHS[parsed.getMonth()]} ${parsed.getDate()}, ${parsed.getFullYear()} at ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
    : '';

  return (
    <div ref={ref} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-2 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-left focus:outline-none focus:border-jeket-gold/50 transition-colors',
          displayValue ? 'text-jeket-silver-bright' : 'text-jeket-silver-muted/50'
        )}
      >
        <Calendar size={14} className="text-jeket-silver-muted shrink-0" />
        {displayValue || placeholder}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 bg-jeket-black-surface border border-jeket-gold/20 rounded-xl shadow-jeket p-4 w-[280px]">
          {/* Month/year nav */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-jeket-black-elevated text-jeket-silver-muted hover:text-jeket-silver-bright">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-jeket-silver-bright">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-jeket-black-elevated text-jeket-silver-muted hover:text-jeket-silver-bright">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {DAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-medium text-jeket-silver-muted py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className={cn(
                    'w-8 h-8 mx-auto rounded-lg text-xs flex items-center justify-center transition-colors',
                    isSelected(day)
                      ? 'bg-jeket-gold text-jeket-black-base font-semibold'
                      : isToday(day)
                        ? 'bg-jeket-gold/10 text-jeket-gold font-medium'
                        : 'text-jeket-silver-bright hover:bg-jeket-black-elevated'
                  )}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Time picker */}
          <div className="mt-3 pt-3 border-t border-jeket-gold/10 flex items-center gap-2">
            <Clock size={14} className="text-jeket-silver-muted shrink-0" />
            <select
              value={hour}
              onChange={(e) => handleTimeChange(Number(e.target.value), minute)}
              className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-2 py-1.5 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>
                  {pad(h)}
                </option>
              ))}
            </select>
            <span className="text-jeket-silver-muted">:</span>
            <select
              value={minute}
              onChange={(e) => handleTimeChange(hour, Number(e.target.value))}
              className="bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-2 py-1.5 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            >
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                <option key={m} value={m}>
                  {pad(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
